import React, { useCallback, useState } from 'react'

import { Box } from '@mui/material'
import { useSelector } from 'react-redux'

import ItemRequestActionModal from '../../../../shared/components/ItemRequestActionModal/ItemRequestActionModal.component'
import ReadyToPurchaseTable from '../../../../shared/components/ReadyToPurchaseTable/ReadyToPurchaseTable.component'
import Tabs from '../../../../shared/components/Tabs/Tabs.component'

import { getPurchaseReadyItems } from '../../../../redux/states/admin/getPurchaseReadyItems/getPurchaseReadyItems.slice'
import {
  ItemRequest,
  OfferRequestDetails
} from '../../../../redux/states/admin/getPurchaseReadyItems/getPurchaseReadyItems.types'
import { updateItemRequestTrackingDetails } from '../../../../redux/states/offers/updateItemRequestTrackingDetails/updateItemRequestTrackingDetails.slice'
import { UpdateItemRequestTrackingDetailsRequestPayload } from '../../../../redux/states/offers/updateItemRequestTrackingDetails/updateItemRequestTrackingDetails.types'
import { RootState } from '../../../../redux/store/store.types'
import {
  FilterStatus,
  deliveredToShopperStatuses,
  deliveredToTravelerAddressStatuses,
  purchasedByAdminStatuses
} from '../../../../shared/components/ReadyToPurchaseTable/ReadyToPurchaseTable.dictionary'
import status from '../../../../shared/functions/Status/status.dictionaries'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import styles from './ReadyToPurchase.styles'
import { ReadyToPurchaseProps } from './ReadyToPurchase.types'

const ReadyToPurchase: React.FC<ReadyToPurchaseProps> = (props: ReadyToPurchaseProps) => {
  const { setReadyToPurchaseTabIndex, readyToPurchaseTabIndex, searchQuery } = props

  const [selectedItemRequest, setSelectedItemRequest] = useState<
    | (ItemRequest & {
        offerRequestDetails: OfferRequestDetails
      })
    | null
  >(null)
  const [showActionModal, setShowActionModal] = useState(false)

  const dispatch = useAppDispatch()

  const { loading: updateTrackingLoading } = useSelector((state: RootState) => state.updateItemRequestTrackingDetails)
  const { loading: getPurchaseReadyItemsLoading } = useSelector((state: RootState) => state.getPurchaseReadyItems)

  const handleRowClick = useCallback((row: unknown, _index: number) => {
    const typedRow = row as ItemRequest & {
      offerRequestDetails: OfferRequestDetails
    }

    setSelectedItemRequest(typedRow)
    setShowActionModal(true)
  }, [])

  const handleBuy = useCallback(() => {
    setShowActionModal(false)
    setSelectedItemRequest(null)
  }, [])

  const handleGetPurchaseReadyItems = useCallback(
    (status: string) => {
      dispatch(
        getPurchaseReadyItems({
          request: {
            page: 1,
            size: 10,
            status: status,
            searchQuery: ''
          },
          onSuccess: () => {
            setShowActionModal(false)
            setSelectedItemRequest(null)
          }
        })
      )
    },
    [dispatch]
  )

  const handleConfirmItemBought = useCallback(() => {
    if (!selectedItemRequest) return

    const payload: UpdateItemRequestTrackingDetailsRequestPayload = {
      request: {
        itemRequestId: selectedItemRequest.id.toString(),
        status: status.deliveryStatus.purchasedByAdmin
      },
      onSuccess: () => {
        handleGetPurchaseReadyItems(status.itemRequestStatus.readyForPurchase)
      }
    }

    dispatch(updateItemRequestTrackingDetails(payload))
  }, [selectedItemRequest, dispatch, handleGetPurchaseReadyItems])

  const handleConfirmDelivery = useCallback(() => {
    if (!selectedItemRequest) return

    const payload: UpdateItemRequestTrackingDetailsRequestPayload = {
      request: {
        itemRequestId: selectedItemRequest.id.toString(),
        status: status.deliveryStatus.confirmedDeliveredToTravelerAddressByAdmin
      },
      onSuccess: () => {
        const readyForPurchaseStatusesString = status.itemRequestStatus.readyForPurchase
        const purchasedStatusesString = purchasedByAdminStatuses.join(',')
        const deliveredToTravelerAddressStatusesString = deliveredToTravelerAddressStatuses.join(',')
        const deliveredToShopperStatusesString = deliveredToShopperStatuses.join(',')

        // Refresh the list based on the current tab context
        switch (readyToPurchaseTabIndex) {
          case 0:
            handleGetPurchaseReadyItems(readyForPurchaseStatusesString)
            break

          case 1:
            // Purchased tab
            handleGetPurchaseReadyItems(purchasedStatusesString)
            break

          case 2:
            // Delivered to traveler address tab
            handleGetPurchaseReadyItems(deliveredToTravelerAddressStatusesString)
            break

          case 3:
            // Delivered to shopper tab
            handleGetPurchaseReadyItems(deliveredToShopperStatusesString)
            break
        }
      }
    }

    dispatch(updateItemRequestTrackingDetails(payload))
  }, [selectedItemRequest, dispatch, handleGetPurchaseReadyItems, readyToPurchaseTabIndex])

  const handleDismiss = useCallback(() => {
    setShowActionModal(false)
    setSelectedItemRequest(null)
  }, [])

  const handleTabChange = (newValue: number) => {
    setReadyToPurchaseTabIndex(newValue)
  }

  const tabs = [
    {
      label: 'Ready to Purchase',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.readyForPurchase}
          searchQuery={searchQuery}
        />
      )
    },
    {
      label: 'Purchased',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.purchased}
          searchQuery={searchQuery}
        />
      )
    },
    {
      label: 'Delivered to Traveler',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.deliveredToTravelerAddress}
          searchQuery={searchQuery}
        />
      )
    },
    {
      label: 'Delivered to Shopper',
      content: (
        <ReadyToPurchaseTable
          onRowClick={handleRowClick}
          filterStatus={FilterStatus.deliveredToShopper}
          searchQuery={searchQuery}
        />
      )
    }
  ]

  return (
    <Box sx={styles.container}>
      <Tabs tabs={tabs} value={readyToPurchaseTabIndex} onChange={handleTabChange} />

      {selectedItemRequest && (
        <ItemRequestActionModal
          open={showActionModal}
          onClose={handleDismiss}
          itemRequest={selectedItemRequest}
          onBuy={handleBuy}
          onConfirmItemBought={handleConfirmItemBought}
          onConfirmDelivery={handleConfirmDelivery}
          onDismiss={handleDismiss}
          refreshTable={() => {
            // Refresh according to current tab when a secondary action triggers refresh
            if (readyToPurchaseTabIndex === 0) {
              handleGetPurchaseReadyItems(status.itemRequestStatus.readyForPurchase)
            } else if (readyToPurchaseTabIndex === 1) {
              const purchasedStatusesString = purchasedByAdminStatuses.join(',')
              handleGetPurchaseReadyItems(purchasedStatusesString)
            } else if (readyToPurchaseTabIndex === 2) {
              const deliveredToTravelerAddressStatusesString = deliveredToTravelerAddressStatuses.join(',')
              handleGetPurchaseReadyItems(deliveredToTravelerAddressStatusesString)
            } else if (readyToPurchaseTabIndex === 3) {
              const deliveredToShopperStatusesString = deliveredToShopperStatuses.join(',')
              handleGetPurchaseReadyItems(deliveredToShopperStatusesString)
            }
          }}
          loading={false}
          confirmItemBoughtLoading={updateTrackingLoading || getPurchaseReadyItemsLoading}
        />
      )}
    </Box>
  )
}

export default ReadyToPurchase
